import React, { useState } from 'react';
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, X, Database, Car } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AddVehicleDataForm = ({ onVehicleDataAdded }) => {
  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm();
  const [loading, setLoading] = useState(false);
  const [engineTypes, setEngineTypes] = useState([]);
  const [newEngineType, setNewEngineType] = useState('');

  // Watch form values for preview
  const watchedValues = watch();

  const addEngineType = () => {
    if (newEngineType.trim() && !engineTypes.includes(newEngineType.trim())) {
      const updated = [...engineTypes, newEngineType.trim()];
      setEngineTypes(updated);
      setValue('engine_types', updated);
      setNewEngineType('');
    }
  };

  const removeEngineType = (typeToRemove) => {
    const updated = engineTypes.filter(type => type !== typeToRemove);
    setEngineTypes(updated);
    setValue('engine_types', updated);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEngineType();
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      const payload = {
        make: data.make.trim(),
        model: data.model.trim(),
        series: data.series.trim(),
        year_range: data.year_range?.trim() || null,
        engine_types: engineTypes.length > 0 ? engineTypes : null
      };

      console.log('Submitting vehicle data:', payload);

      await axios.post(`${BACKEND_URL}/api/vehicle-data`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success(`Successfully added ${data.make} ${data.model} ${data.series} to vehicle database!`);
      
      // Reset form
      reset();
      setEngineTypes([]);
      
      // Trigger a custom event to notify other components to refresh their data
      window.dispatchEvent(new CustomEvent('vehicleDataUpdated'));
      
      if (onVehicleDataAdded) {
        onVehicleDataAdded();
      }
      
    } catch (error) {
      console.error('Error adding vehicle data:', error);
      
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'This vehicle data already exists');
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.');
      } else {
        toast.error('Failed to add vehicle data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="flex items-center justify-center mb-3">
          <Database className="h-8 w-8 text-blue-600 mr-2" />
          <Car className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Add New Vehicle Data</h3>
        <p className="text-sm text-gray-600 mt-1">
          Add a new Make → Model → Series combination to the vehicle database
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Make */}
          <div>
            <Label htmlFor="make" className="text-sm font-medium text-gray-700">
              Vehicle Make *
            </Label>
            <Input
              id="make"
              {...register("make", { 
                required: "Vehicle make is required",
                minLength: { value: 2, message: "Make must be at least 2 characters" }
              })}
              placeholder="e.g., BMW, Mercedes-Benz"
              className="mt-1"
            />
            {errors.make && (
              <p className="text-sm text-red-600 mt-1">{errors.make.message}</p>
            )}
          </div>

          {/* Model */}
          <div>
            <Label htmlFor="model" className="text-sm font-medium text-gray-700">
              Vehicle Model *
            </Label>
            <Input
              id="model"
              {...register("model", { 
                required: "Vehicle model is required",
                minLength: { value: 1, message: "Model is required" }
              })}
              placeholder="e.g., 320i, C200"
              className="mt-1"
            />
            {errors.model && (
              <p className="text-sm text-red-600 mt-1">{errors.model.message}</p>
            )}
          </div>

          {/* Series */}
          <div>
            <Label htmlFor="series" className="text-sm font-medium text-gray-700">
              Vehicle Series *
            </Label>
            <Input
              id="series"
              {...register("series", { 
                required: "Vehicle series is required",
                minLength: { value: 1, message: "Series is required" }
              })}
              placeholder="e.g., F30, W205"
              className="mt-1"
            />
            {errors.series && (
              <p className="text-sm text-red-600 mt-1">{errors.series.message}</p>
            )}
          </div>
        </div>

        {/* Year Range */}
        <div>
          <Label htmlFor="year_range" className="text-sm font-medium text-gray-700">
            Year Range (Optional)
          </Label>
          <Input
            id="year_range"
            {...register("year_range")}
            placeholder="e.g., 2012-2018, 2019-Present"
            className="mt-1"
          />
          <p className="text-xs text-gray-500 mt-1">
            Specify the production years for this series
          </p>
        </div>

        {/* Engine Types */}
        <div>
          <Label className="text-sm font-medium text-gray-700">
            Engine Types (Optional)
          </Label>
          <div className="mt-2">
            <div className="flex space-x-2 mb-3">
              <Input
                value={newEngineType}
                onChange={(e) => setNewEngineType(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="e.g., 2.0L Turbo, 3.0L V6"
                className="flex-1"
              />
              <Button
                type="button"
                onClick={addEngineType}
                variant="outline"
                size="sm"
                disabled={!newEngineType.trim()}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Engine Types Display */}
            {engineTypes.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {engineTypes.map((type, index) => (
                  <Badge key={index} variant="secondary" className="flex items-center gap-1">
                    {type}
                    <button
                      type="button"
                      onClick={() => removeEngineType(type)}
                      className="ml-1 text-gray-500 hover:text-gray-700"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            
            <p className="text-xs text-gray-500 mt-1">
              Add common engine types for this vehicle series
            </p>
          </div>
        </div>

        {/* Preview Card */}
        {(watchedValues.make || watchedValues.model || watchedValues.series) && (
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-blue-800">Preview</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-sm">
                <span className="font-medium text-blue-900">
                  {watchedValues.make || 'Make'} → {watchedValues.model || 'Model'} → {watchedValues.series || 'Series'}
                </span>
                {watchedValues.year_range && (
                  <span className="text-blue-700 ml-2">({watchedValues.year_range})</span>
                )}
              </div>
              {engineTypes.length > 0 && (
                <div className="mt-2">
                  <span className="text-xs text-blue-700">Engine Types: </span>
                  <span className="text-xs text-blue-800">{engineTypes.join(', ')}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Submit Button */}
        <div className="pt-2">
          <Button 
            type="submit" 
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Adding Vehicle Data...
              </>
            ) : (
              <>
                <Database className="h-4 w-4 mr-2" />
                Add to Vehicle Database
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Info Note */}
      <div className="bg-gray-50 p-4 rounded-lg border">
        <h4 className="text-sm font-medium text-gray-800 mb-2">ℹ️ About Vehicle Data</h4>
        <ul className="text-xs text-gray-600 space-y-1">
          <li>• This adds new Make → Model → Series combinations to your database</li>
          <li>• Once added, these will appear in cascading dropdowns throughout the system</li>
          <li>• Duplicate entries are automatically prevented</li>
          <li>• Engine types help identify specific variants of the same series</li>
        </ul>
      </div>
    </div>
  );
};

export default AddVehicleDataForm;